import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useProducerTerms } from '@/hooks/useProducerTerms';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/hooks/use-toast';
import { FileText, ExternalLink, LogOut, AlertCircle, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';

/**
 * Full-screen terms acceptance page — rendered WITHOUT portal chrome
 * (bottom nav / FAB are hidden via HIDE_CHROME_PATTERNS in AppShell).
 *
 * Redirects back to the portal (or returnTo path) after acceptance.
 */
export default function ProducerTermsPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOut } = useAuth();

  const {
    requiredVersion,
    requiredTitle,
    isAccepted,
    isLoading,
    acceptTerms,
    isAccepting,
    acceptError,
    error,
    refetch,
  } = useProducerTerms(farmId);

  const returnTo = searchParams.get('returnTo');
  const defaultRedirect = `/app/producer/farm/${farmId}`;
  const redirectTarget = returnTo ? decodeURIComponent(returnTo) : defaultRedirect;

  // During local E2E runs we may want to skip the interactive terms gate
  // because seeds should have accepted terms upstream. This flag is
  // intentionally opt-in via Vite env to avoid weakening production logic.
  const disableE2ETerms = (import.meta.env.VITE_DISABLE_TERMS_FOR_E2E ?? "") === "1";

  // Debug: surface compile-time env at runtime for E2E troubleshooting
  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[E2E] VITE_DISABLE_TERMS_FOR_E2E=', import.meta.env.VITE_DISABLE_TERMS_FOR_E2E);
  }

  useEffect(() => {
    if (disableE2ETerms && farmId) {
      // Bypass gate in E2E/dev for deterministic tests
      navigate(redirectTarget, { replace: true });
    }
  }, [disableE2ETerms, farmId, navigate, redirectTarget]);

  // If already accepted, redirect back
  useEffect(() => {
    if (!isLoading && isAccepted && farmId) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isLoading, isAccepted, farmId, navigate, redirectTarget]);

  const handleAccept = async () => {
    if (!requiredVersion) return;
    try {
      await acceptTerms(requiredVersion);
      toast({
        title: 'Villkor godkända',
        description: 'Du har godkänt värdvillkoren och kan nu använda portalen.',
      });
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      console.error('[ProducerTermsPage] Failed to accept terms:', err);
      toast({
        title: 'Något gick fel',
        description: 'Kunde inte spara godkännandet. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
            </div>
            <CardTitle>Något gick fel</CardTitle>
            <CardDescription>
              Vi kunde inte verifiera dina villkor. Försök igen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {(error as Error).message || 'Ett oväntat fel uppstod.'}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-3">
              <Button onClick={() => refetch()} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Försök igen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If already accepted, render nothing (useEffect will redirect)
  if (isAccepted) return null;

  // ── Gate UI ──
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg shadow-xl border-2">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-7 w-7 text-primary" />
            </div>
          </div>
          <CardTitle className="font-display text-2xl">
            Vi har uppdaterat våra villkor
          </CardTitle>
          <CardDescription className="text-base mt-2">
            För att fortsätta använda värdportalen behöver du godkänna den senaste versionen av våra värdvillkor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Version info */}
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Aktuell version</p>
            <p className="font-medium text-lg">
              {requiredTitle || 'Värdvillkor'} v{requiredVersion}
            </p>
          </div>

          {/* TL;DR */}
          <div className="text-sm space-y-3">
            <p className="font-medium text-foreground">TL;DR – det här innebär i korthet:</p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Du ansvarar för att information, priser, lager och innehåll är korrekt.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Du ansvarar för lagkrav (t.ex. livsmedelssäkerhet) och leverans/upplevelse till kund.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Farmly tillhandahåller plattformen och kan visa ditt innehåll i marknadsföring.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Vid försäljning gäller Farmlys aktuella avgifter/provisioner.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Du kan avsluta när som helst, och Farmly kan stänga av vid missbruk.</span>
              </li>
            </ul>
          </div>

          {/* Accept error */}
          {acceptError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {(acceptError as Error).message || 'Kunde inte spara godkännandet.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2">
            <Button
              onClick={handleAccept}
              className="w-full"
              size="lg"
              disabled={isAccepting}
            >
              {isAccepting ? 'Sparar…' : 'Jag godkänner'}
            </Button>
            <Button variant="outline" className="w-full" asChild>
              <a
                href="/legal/producer-terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Öppna villkoren i ny flik
              </a>
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logga ut
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
